import { useParams, Navigate } from "react-router-dom";
import { APPLIANCES, NEIGHBORHOODS, BRANDS } from "@/data/seo";
import CityService from "@/pages/CityService";
import BrandAppliance from "@/pages/BrandAppliance";
import TripleCombo from "@/pages/TripleCombo";

/**
 * Dispatcher for `/:combo` URLs (longest-match first so triple combos don't get
 * mis-parsed as location combos).
 *
 *  - `{brand}-{appliance}-repair-{neighborhood}` → TripleCombo  (~392 valid pages)
 *  - `{appliance}-repair-{neighborhood}`         → CityService  (96 pages)
 *  - `{brand}-{appliance}-repair`                → BrandAppliance (~49 valid pages)
 *  - anything else                               → redirect home
 */
export default function ComboDispatcher() {
  const { combo } = useParams();
  if (!combo) return <Navigate to="/" replace />;

  // The slug always contains `-repair-` or ends with `-repair`. Parse with right-most match.
  const m = combo.match(/^(.+?)-repair-(.+)$/);
  if (m) {
    const left = m[1];
    const right = m[2];
    const hood = NEIGHBORHOODS.find((n) => n.slug === right);
    if (hood) {
      // 1) appliance-repair-hood
      const appOnly = APPLIANCES.find((a) => a.slug === left);
      if (appOnly) return <CityService app={appOnly} hood={hood} />;
      // 2) brand-appliance-repair-hood
      for (const brand of BRANDS) {
        const prefix = `${brand.slug}-`;
        if (left.startsWith(prefix)) {
          const appSlug = left.slice(prefix.length);
          const app = APPLIANCES.find((a) => a.slug === appSlug);
          const supported = app && brand.common_appliances.some(
            (ca) => ca.toLowerCase() === app.name.toLowerCase()
          );
          if (supported) return <TripleCombo brand={brand} app={app} hood={hood} />;
        }
      }
    }
  }

  // brand-appliance-repair (no neighborhood)
  if (combo.endsWith("-repair")) {
    const base = combo.slice(0, -"-repair".length);
    for (const brand of BRANDS) {
      const prefix = `${brand.slug}-`;
      if (base.startsWith(prefix)) {
        const appSlug = base.slice(prefix.length);
        const app = APPLIANCES.find((a) => a.slug === appSlug);
        const supported = app && brand.common_appliances.some(
          (ca) => ca.toLowerCase() === app.name.toLowerCase()
        );
        if (supported) return <BrandAppliance brand={brand} app={app} />;
      }
    }
  }

  return <Navigate to="/" replace />;
}
